import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Sparkles,
  Upload,
  FileText,
  X,
  ArrowRight,
  ArrowLeft,
  Loader2,
  CheckCircle2,
  Send,
  Bot,
  Stethoscope,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import {
  createConsultation,
  analyzeConsultation,
  pollConsultationAnalysis,
} from "@/lib/adapters/consultations";

// ----------------------- static data (ported subset) -----------------------

const TREATMENT_CATEGORIES = [
  { value: "rct", label: "RCT", problemType: "rct_required" },
  { value: "implant", label: "Implant", problemType: "implant_case" },
  { value: "extraction", label: "Extraction", problemType: "extraction_case" },
  { value: "crown_bridge", label: "Crown & Bridge", problemType: "crown_bridge" },
  { value: "orthodontics", label: "Orthodontics", problemType: "orthodontics" },
  { value: "scaling", label: "Scaling", problemType: "scaling" },
  { value: "denture", label: "Denture", problemType: "denture" },
  { value: "pediatric", label: "Pediatric", problemType: "pediatric" },
  { value: "oral_surgery", label: "Oral Surgery", problemType: "oral_surgery" },
  { value: "periodontal", label: "Periodontal", problemType: "periodontal" },
  { value: "cosmetic", label: "Cosmetic", problemType: "cosmetic" },
  { value: "other", label: "Other", problemType: "other" },
];

const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-", "Unknown"];

const EMERGENCY_KEYWORDS = /\b(severe|acute|bleeding|swelling|trauma|fracture|emergency|abscess|fever|unconscious|face|jaw)\b/i;

const MAX_XRAY_MB = 15;
const XRAY_MIME = ["application/pdf", "image/png", "image/jpeg", "image/jpg"];

// Question definitions (id, prompt builder, placeholder, optional flag, mapping target)
const QUESTIONS = {
  pain_duration: {
    prompt: (ctx) =>
      ctx.isEmergency
        ? "When did the pain or acute symptoms begin, and how severe are they now?"
        : ctx.chiefComplaint
          ? `Regarding "${ctx.chiefComplaint.slice(0, 60)}${ctx.chiefComplaint.length > 60 ? "…" : ""}" — how long has the patient had pain or discomfort?`
          : "How long has the patient had pain or discomfort?",
    placeholder: "e.g. 3 days, intermittent for 2 weeks",
    map: (text, next) => {
      if (text) next.symptoms.push(`Pain duration — ${text.slice(0, 80)}`);
      next.doctorNotes.push(`Pain duration: ${text}`);
    },
  },
  swelling: {
    prompt: () => "Is there any swelling? Please describe the location and severity.",
    placeholder: "e.g. No / mild buccal swelling near #36",
    map: (text, next) => {
      if (text && !/^no/i.test(text) && !/^none/i.test(text)) next.symptoms.push("Swelling");
      next.doctorNotes.push(`Swelling: ${text}`);
    },
  },
  swelling_airway: {
    prompt: () => "Is there facial swelling? Note any airway, eye, or floor-of-mouth involvement.",
    placeholder: "e.g. Buccal swelling only / no airway compromise",
    map: (text, next) => {
      if (text && !/^no/i.test(text)) next.symptoms.push("Swelling / airway");
      next.doctorNotes.push(`Swelling / airway: ${text}`);
    },
  },
  fever: {
    prompt: () => "Is there fever or systemic illness?",
    placeholder: "e.g. 38.5°C since yesterday / No fever",
    map: (text, next) => {
      if (text && !/^no/i.test(text)) next.symptoms.push("Fever reported");
      next.doctorNotes.push(`Fever: ${text}`);
    },
  },
  bleeding: {
    prompt: () => "Is there active or recent bleeding?",
    placeholder: "e.g. Controlled oozing / No bleeding",
    map: (text, next) => {
      if (text && !/^no/i.test(text)) next.symptoms.push("Bleeding reported");
      next.doctorNotes.push(`Bleeding: ${text}`);
    },
  },
  diabetes: {
    prompt: (ctx) =>
      ctx.isImplant
        ? "Does the patient have diabetes, bisphosphonate use, or other systemic risk factors?"
        : "Does the patient have diabetes or blood sugar issues?",
    placeholder: "e.g. Type 2 DM on metformin / No",
    map: (text, next) => next.medicalHistory.push(`Diabetes / systemic risk: ${text || "Not reported"}`),
  },
  allergies: {
    prompt: () => "Any known drug or material allergies?",
    placeholder: "e.g. Penicillin allergy / NKDA / No",
    map: (text, next) => next.medicalHistory.push(`Allergies: ${text || "NKDA"}`),
  },
  smoking: {
    prompt: () => "Smoking or tobacco use?",
    placeholder: "e.g. Non-smoker / 5 cigarettes per day",
    map: (text, next) => next.medicalHistory.push(`Tobacco: ${text || "Not reported"}`),
  },
  medications: {
    prompt: () => "Current medications?",
    placeholder: "e.g. Amlodipine 5 mg OD / None",
    map: (text, next) => next.medicalHistory.push(`Medications: ${text || "No regular medications"}`),
  },
  previous_treatment: {
    prompt: () => "Previous dental treatment related to this area?",
    placeholder: "e.g. RCT #36 in 2019 / First visit for this tooth",
    map: (text, next) => next.dentalHistory.push(`Prior treatment: ${text}`),
  },
  tooth_findings: {
    prompt: (ctx) => {
      if (ctx.isImplant) return "Adjacent teeth, occlusion, and space notes relevant to implant planning?";
      if (ctx.isExtraction) return "Tooth-specific findings — tooth number, mobility, caries, restoration status?";
      return "Tooth-specific findings — tooth number, percussion response, mobility, caries, and restoration status?";
    },
    placeholder: "e.g. #36 tender to percussion, class II caries, MOD amalgam",
    map: (text, next) => next.doctorNotes.push(`Exam findings: ${text}`),
  },
  radiograph_findings: {
    prompt: () => "From the uploaded radiograph, what key findings do you see (periapical lesion, canals, bone level, impaction)?",
    placeholder: "e.g. Periapical radiolucency #36 / curved canals",
    map: (text, next) => next.doctorNotes.push(`Radiograph findings: ${text}`),
  },
  mobility: {
    prompt: () => "Tooth mobility and restorability?",
    placeholder: "e.g. Grade II mobility #48 / non-restorable",
    map: (text, next) => next.doctorNotes.push(`Mobility: ${text}`),
  },
  infection_signs: {
    prompt: () => "Signs of infection such as pus, abscess, or fever?",
    placeholder: "e.g. Localized abscess / No infection",
    map: (text, next) => {
      if (text && !/^no/i.test(text)) next.symptoms.push("Infection signs reported");
      next.doctorNotes.push(`Infection: ${text}`);
    },
  },
  impaction: {
    prompt: () => "Impaction or surgical difficulty (angulation, proximity to nerve)?",
    placeholder: "e.g. Mesioangular impaction / straightforward",
    map: (text, next) => next.doctorNotes.push(`Impaction: ${text}`),
  },
  bleeding_risk: {
    prompt: () => "Bleeding risk or anticoagulant use?",
    placeholder: "e.g. Aspirin / No anticoagulants",
    map: (text, next) => next.medicalHistory.push(`Bleeding risk: ${text}`),
  },
  missing_duration: {
    prompt: () => "How long has the tooth been missing?",
    placeholder: "e.g. Extracted 6 months ago",
    map: (text, next) => next.dentalHistory.push(`Missing tooth duration: ${text}`),
  },
  bone_quality: {
    prompt: () => "Bone quality and ridge assessment at the implant site?",
    placeholder: "e.g. Adequate width, D2 bone",
    map: (text, next) => next.doctorNotes.push(`Bone quality: ${text}`),
  },
  additional_notes: {
    prompt: () => "Any other clinical observations before we analyze?",
    placeholder: 'Optional — exam notes or type "none"',
    optional: true,
    map: (text, next) => {
      if (text && !/^none/i.test(text)) next.doctorNotes.push(`Additional notes: ${text}`);
    },
  },
};

const SEQUENCES = {
  emergency: ["fever", "swelling_airway", "bleeding", "pain_duration", "diabetes", "allergies", "smoking", "medications", "previous_treatment", "tooth_findings", "radiograph_findings", "additional_notes"],
  rct: ["pain_duration", "swelling", "diabetes", "allergies", "smoking", "medications", "previous_treatment", "tooth_findings", "radiograph_findings", "additional_notes"],
  implant: ["missing_duration", "bone_quality", "diabetes", "allergies", "smoking", "medications", "previous_treatment", "tooth_findings", "radiograph_findings", "additional_notes"],
  extraction: ["pain_duration", "mobility", "infection_signs", "impaction", "diabetes", "allergies", "smoking", "medications", "previous_treatment", "tooth_findings", "bleeding_risk", "radiograph_findings", "additional_notes"],
  default: ["pain_duration", "swelling", "diabetes", "allergies", "smoking", "medications", "previous_treatment", "tooth_findings", "radiograph_findings", "additional_notes"],
};

function buildContext(intake) {
  const cat = intake.treatmentCategory;
  const isEmergency = EMERGENCY_KEYWORDS.test(intake.chiefComplaint || "");
  return {
    chiefComplaint: intake.chiefComplaint || "",
    isEmergency,
    isRct: cat === "rct",
    isImplant: cat === "implant",
    isExtraction: cat === "extraction",
    hasXray: intake.xrayFiles.length > 0,
  };
}

function getSequence(ctx) {
  if (ctx.isEmergency) return SEQUENCES.emergency;
  if (ctx.isRct) return SEQUENCES.rct;
  if (ctx.isImplant) return SEQUENCES.implant;
  if (ctx.isExtraction) return SEQUENCES.extraction;
  return SEQUENCES.default;
}

function expandQuestions(intake) {
  const ctx = buildContext(intake);
  const seq = getSequence(ctx);
  return seq
    .filter((id) => {
      if (id === "radiograph_findings" && !ctx.hasXray) return false;
      return QUESTIONS[id];
    })
    .map((id) => {
      const def = QUESTIONS[id];
      return { id, prompt: def.prompt(ctx), placeholder: def.placeholder, optional: def.optional };
    });
}

function inferEmergencyLevel(chief) {
  if (!chief) return "Normal";
  if (/\b(severe|acute|bleeding|swelling|trauma|fracture|emergency|abscess|unconscious)\b/i.test(chief)) return "Critical";
  if (/\b(pain|fever|throbbing|infected|infection|hurts?|ache)\b/i.test(chief)) return "Urgent";
  return "Normal";
}

// ----------------------- validation -----------------------

function validateIntake(intake) {
  const errors = {};
  const trimmed = intake.fullName.trim();
  if (trimmed.length < 2 || trimmed.length > 100) errors.fullName = "Name should be 2–100 characters";
  const age = Number(intake.age);
  if (!age || age < 1 || age > 120) errors.age = "Age must be 1–120";
  if (!intake.gender) errors.gender = "Select a gender";
  if (intake.bloodPressure) {
    if (!/^\d{2,3}\/\d{2,3}$/.test(intake.bloodPressure.trim())) errors.bloodPressure = "Use format e.g. 120/80";
  } else {
    errors.bloodPressure = "Blood pressure is required (e.g. 120/80)";
  }
  if (!intake.treatmentCategory) errors.treatmentCategory = "Choose a treatment category";
  if (intake.treatmentCategory === "other" && !intake.otherCategorySpecify.trim()) {
    errors.otherCategorySpecify = "Please specify the treatment";
  }
  const cc = intake.chiefComplaint.trim();
  if (cc.length < 10 || cc.length > 500) errors.chiefComplaint = "Chief complaint should be 10–500 characters";
  if (!intake.xrayFiles.length) errors.xrayFiles = "X-ray upload is required";
  return errors;
}

// ----------------------- localStorage autosave -----------------------

const LS_KEY = "dyntra_ai_consult_draft_v1";
function readDraft() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}
function writeDraft(payload) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(payload)); } catch { /* ignore quota */ }
}
function clearDraft() {
  try { localStorage.removeItem(LS_KEY); } catch { /* ignore */ }
}

// ============================================================================

const initialIntake = {
  fullName: "",
  age: "",
  gender: "",
  bloodGroup: "",
  bloodPressure: "",
  treatmentCategory: "",
  otherCategorySpecify: "",
  chiefComplaint: "",
  xrayFiles: [], // { name, type, size, file }
};

export default function AiConsultantNew() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [intake, setIntake] = useState(initialIntake);
  const [answers, setAnswers] = useState({});
  const [errors, setErrors] = useState({});
  const [phase, setPhase] = useState("idle"); // idle | submitting | analyzing | done
  const [analysisMsg, setAnalysisMsg] = useState("");

  const set = (k, v) => setIntake((it) => ({ ...it, [k]: v }));

  // Load draft on mount
  useEffect(() => {
    const d = readDraft();
    if (d?.intake) {
      // Rehydrate everything except File objects (which can't be serialized)
      const { xrayFiles, ...rest } = d.intake;
      setIntake({ ...initialIntake, ...rest, xrayFiles: [] });
    }
    if (d?.answers) setAnswers(d.answers);
    if (d?.step) setStep(d.step);
  }, []);

  // Autosave every change (debounced by 500ms)
  useEffect(() => {
    const t = setTimeout(() => {
      const { xrayFiles, ...rest } = intake;
      writeDraft({
        intake: { ...rest, xrayFiles: xrayFiles.map((x) => ({ name: x.name, size: x.size, type: x.type })) },
        answers,
        step,
      });
    }, 500);
    return () => clearTimeout(t);
  }, [intake, answers, step]);

  // Leave-guard
  useEffect(() => {
    const handler = (e) => {
      if (phase === "submitting" || phase === "analyzing") {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [phase]);

  const onFilePick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!XRAY_MIME.includes(file.type) && !/\.(png|jpe?g|pdf)$/i.test(file.name)) {
      toast.error("X-ray must be PDF, PNG or JPG");
      return;
    }
    if (file.size > MAX_XRAY_MB * 1024 * 1024) {
      toast.error(`X-ray must be ≤ ${MAX_XRAY_MB} MB`);
      return;
    }
    set("xrayFiles", [{ name: file.name, size: file.size, type: file.type, file }]);
    setErrors((er) => ({ ...er, xrayFiles: undefined }));
  };

  const removeXray = () => set("xrayFiles", []);

  const advanceToChat = () => {
    const er = validateIntake(intake);
    setErrors(er);
    if (Object.keys(er).length) {
      toast.error("Please fix the highlighted fields");
      const firstKey = Object.keys(er)[0];
      const el = document.querySelector(`[data-field="${firstKey}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setStep(2);
  };

  const questions = useMemo(() => (step === 2 ? expandQuestions(intake) : []), [step, intake]);

  const submit = async () => {
    if (phase !== "idle") return;
    setPhase("submitting");
    setAnalysisMsg("Uploading case details…");

    // Compile answers into buckets
    const buckets = { symptoms: [], medicalHistory: [], dentalHistory: [], doctorNotes: [] };
    for (const q of questions) {
      const raw = (answers[q.id] || "").trim();
      const def = QUESTIONS[q.id];
      if (!def) continue;
      def.map(raw, buckets);
    }

    // Fallbacks so the backend length checks pass
    const symptoms = buckets.symptoms.length ? buckets.symptoms : [intake.chiefComplaint.trim().slice(0, 80) || "As per chief complaint"];
    const medicalHistory = buckets.medicalHistory.join("\n") || "No significant medical history reported during intake.";
    const dentalHistory = buckets.dentalHistory.join("\n") || "No prior dental treatment reported for this complaint.";
    const doctorNotes = buckets.doctorNotes.join("\n") || `Intake summary — ${intake.chiefComplaint.trim()}`;

    const catDef = TREATMENT_CATEGORIES.find((c) => c.value === intake.treatmentCategory);
    const dentalProblemTypes = catDef ? [catDef.problemType] : ["other"];

    const fd = new FormData();
    fd.append("fullName", intake.fullName.trim());
    fd.append("age", String(Number(intake.age)));
    fd.append("gender", intake.gender);
    if (intake.bloodGroup) fd.append("bloodGroup", intake.bloodGroup);
    if (intake.bloodPressure) fd.append("bloodPressure", intake.bloodPressure.trim());
    fd.append("dentalProblemTypes", JSON.stringify(dentalProblemTypes));
    if (intake.treatmentCategory === "other" && intake.otherCategorySpecify.trim()) {
      fd.append("otherProblemSpecify", intake.otherCategorySpecify.trim());
    }
    fd.append("chiefComplaint", intake.chiefComplaint.trim());
    fd.append("symptoms", JSON.stringify(symptoms));
    fd.append("medicalHistory", medicalHistory);
    fd.append("dentalHistory", dentalHistory);
    fd.append("doctorNotes", doctorNotes);
    fd.append("emergencyLevel", inferEmergencyLevel(intake.chiefComplaint));
    if (intake.xrayFiles[0]?.file) {
      fd.append("xrayFiles", intake.xrayFiles[0].file, intake.xrayFiles[0].name);
    }

    try {
      const res = await createConsultation(fd);
      const caseId = res?.caseId || res?.case_id || res?.id;
      if (!caseId) throw new Error("No caseId returned");

      setPhase("analyzing");
      setAnalysisMsg("Analyzing case…");
      try {
        await analyzeConsultation(caseId, { force: false });
      } catch { /* analyze failure is non-fatal; poll below will still resolve */ }

      const record = await pollConsultationAnalysis(caseId, {
        maxAttempts: 30,
        intervalMs: 2500,
        onTick: (r, i) => {
          if (i > 0) setAnalysisMsg(`Analyzing case… (${i + 1}/30)`);
        },
      });

      clearDraft();
      setPhase("done");
      toast.success("Analysis complete");
      const finalId = record?.caseId || record?.case_id || record?.id || caseId;
      navigate(`/ai-consultant/${finalId}/report`);
    } catch (err) {
      console.error("[ai-consult] submit failed", err);
      setPhase("idle");
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        toast.error("Please sign in to save your case.");
        auth.setAuthMode?.("login");
        auth.setAuthOpen?.(true);
      } else {
        toast.error("Could not submit — please try again.");
      }
    }
  };

  return (
    <main className="dyntra-container py-10">
      <Link
        to="/ai-consultant"
        data-testid="new-back"
        className="inline-flex items-center gap-2 text-sm font-semibold text-[#4B2AAD] hover:underline"
      >
        <ArrowLeft className="h-4 w-4" /> Back to AI Consultant
      </Link>

      <div className="mt-4 flex items-center gap-3">
        <div className="h-11 w-11 rounded-xl bg-[#F3EFFC] flex items-center justify-center">
          <Sparkles className="h-5 w-5 text-[#4B2AAD]" />
        </div>
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-[#4B2AAD]">Dr. Dyntra AI</p>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-gray-900">New Case Analysis</h1>
        </div>
      </div>

      <StepIndicator step={step} />

      {step === 1 && (
        <IntakeStep
          intake={intake}
          set={set}
          errors={errors}
          onFilePick={onFilePick}
          removeXray={removeXray}
          onNext={advanceToChat}
        />
      )}
      {step === 2 && (
        <ChatStep
          intake={intake}
          questions={questions}
          answers={answers}
          setAnswers={setAnswers}
          onBack={() => setStep(1)}
          onSubmit={submit}
          phase={phase}
          analysisMsg={analysisMsg}
        />
      )}
    </main>
  );
}

function StepIndicator({ step }) {
  return (
    <ol className="mt-6 grid grid-cols-2 gap-3 max-w-md">
      {[
        { n: 1, label: "Quick Intake" },
        { n: 2, label: "Guided AI Chat" },
      ].map((s) => {
        const done = step > s.n;
        const active = step === s.n;
        return (
          <li
            key={s.n}
            data-testid={`wizard-step-${s.n}`}
            className={`flex items-center gap-2 rounded-xl border px-3 py-2 ${active ? "border-[#4B2AAD] bg-[#F3EFFC]" : done ? "border-emerald-300 bg-emerald-50" : "border-gray-200 bg-white"}`}
          >
            <span className={`h-6 w-6 rounded-full flex items-center justify-center text-xs font-bold ${done ? "bg-emerald-500 text-white" : active ? "bg-[#4B2AAD] text-white" : "bg-gray-200 text-gray-500"}`}>
              {done ? <CheckCircle2 className="h-3.5 w-3.5" /> : s.n}
            </span>
            <span className={`text-sm font-semibold ${active ? "text-[#4B2AAD]" : done ? "text-emerald-700" : "text-gray-500"}`}>{s.label}</span>
          </li>
        );
      })}
    </ol>
  );
}

// ----------------------- Step 1: Intake -----------------------

function IntakeStep({ intake, set, errors, onFilePick, removeXray, onNext }) {
  return (
    <section className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 rounded-2xl border border-gray-100 bg-white shadow-sm p-6 sm:p-8">
        <h2 className="font-display text-lg font-bold text-gray-900">Patient details</h2>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field
            label="Full name*"
            testid="intake-fullname"
            value={intake.fullName}
            onChange={(v) => set("fullName", v)}
            error={errors.fullName}
            fieldKey="fullName"
          />
          <Field
            label="Age (years)*"
            testid="intake-age"
            type="number"
            value={intake.age}
            onChange={(v) => set("age", v)}
            error={errors.age}
            fieldKey="age"
          />
          <SelectField
            label="Gender*"
            testid="intake-gender"
            value={intake.gender}
            onChange={(v) => set("gender", v)}
            error={errors.gender}
            fieldKey="gender"
            options={[
              { value: "", label: "Select…" },
              { value: "male", label: "Male" },
              { value: "female", label: "Female" },
              { value: "other", label: "Other" },
              { value: "prefer_not_to_say", label: "Prefer not to say" },
            ]}
          />
          <SelectField
            label="Blood group"
            testid="intake-blood-group"
            value={intake.bloodGroup}
            onChange={(v) => set("bloodGroup", v)}
            options={[{ value: "", label: "Select…" }, ...BLOOD_GROUPS.map((g) => ({ value: g, label: g }))]}
          />
          <Field
            label="Blood pressure*"
            testid="intake-bp"
            value={intake.bloodPressure}
            onChange={(v) => set("bloodPressure", v)}
            error={errors.bloodPressure}
            placeholder="e.g. 120/80"
            fieldKey="bloodPressure"
          />
        </div>

        <h2 className="mt-8 font-display text-lg font-bold text-gray-900">Case details</h2>
        <div className="mt-4">
          <label className="text-xs font-bold uppercase tracking-wider text-gray-500" data-field="treatmentCategory">Treatment category*</label>
          <div className="mt-2 flex flex-wrap gap-2">
            {TREATMENT_CATEGORIES.map((c) => (
              <button
                key={c.value}
                type="button"
                data-testid={`intake-cat-${c.value}`}
                onClick={() => set("treatmentCategory", c.value)}
                className={`rounded-xl border px-3 py-2 text-sm font-semibold transition-colors ${intake.treatmentCategory === c.value ? "border-[#4B2AAD] bg-[#F3EFFC] text-[#4B2AAD]" : "border-gray-200 text-gray-700 hover:border-gray-300"}`}
              >
                {c.label}
              </button>
            ))}
          </div>
          {errors.treatmentCategory && <p className="mt-1.5 text-xs text-red-600">{errors.treatmentCategory}</p>}

          {intake.treatmentCategory === "other" && (
            <div className="mt-3">
              <Field
                label="Specify treatment*"
                testid="intake-other"
                value={intake.otherCategorySpecify}
                onChange={(v) => set("otherCategorySpecify", v)}
                error={errors.otherCategorySpecify}
                fieldKey="otherCategorySpecify"
              />
            </div>
          )}
        </div>

        <div className="mt-6">
          <label className="text-xs font-bold uppercase tracking-wider text-gray-500" data-field="chiefComplaint">
            Chief complaint*
          </label>
          <textarea
            data-testid="intake-cc"
            rows={4}
            value={intake.chiefComplaint}
            onChange={(e) => set("chiefComplaint", e.target.value)}
            placeholder="Describe the patient's main complaint (10–500 characters)"
            className={`mt-1 w-full rounded-xl border ${errors.chiefComplaint ? "border-red-400" : "border-gray-200"} focus:border-[#4B2AAD] focus:ring-2 focus:ring-[#4B2AAD]/15 px-4 py-2.5 outline-none text-sm`}
          />
          <div className="mt-1 flex items-center justify-between text-xs">
            <span className={errors.chiefComplaint ? "text-red-600" : "text-gray-500"}>
              {errors.chiefComplaint || "10–500 characters"}
            </span>
            <span className="text-gray-400">{intake.chiefComplaint.length}/500</span>
          </div>
        </div>

        <div className="mt-6" data-field="xrayFiles">
          <label className="text-xs font-bold uppercase tracking-wider text-gray-500">X-ray upload*</label>
          {intake.xrayFiles.length ? (
            <div className="mt-2 flex items-center gap-3 rounded-xl border border-gray-200 bg-[#F8F9FA] p-3">
              <FileText className="h-5 w-5 text-[#4B2AAD]" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">{intake.xrayFiles[0].name}</p>
                <p className="text-xs text-gray-500">{(intake.xrayFiles[0].size / 1024 / 1024).toFixed(2)} MB</p>
              </div>
              <button
                type="button"
                onClick={removeXray}
                data-testid="intake-xray-remove"
                className="h-8 w-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-500"
                aria-label="Remove x-ray"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <label
              htmlFor="xray-input"
              className={`mt-2 flex flex-col items-center justify-center rounded-xl border-2 border-dashed ${errors.xrayFiles ? "border-red-400" : "border-gray-200"} bg-[#F8F9FA] hover:bg-[#F3EFFC] transition-colors py-8 cursor-pointer`}
            >
              <Upload className="h-6 w-6 text-[#4B2AAD]" />
              <p className="mt-2 text-sm font-semibold text-gray-900">Upload X-ray</p>
              <p className="text-xs text-gray-500">PDF, PNG or JPG · Max {MAX_XRAY_MB} MB</p>
              <input
                id="xray-input"
                data-testid="intake-xray-input"
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/*"
                onChange={onFilePick}
                className="hidden"
              />
            </label>
          )}
          {errors.xrayFiles && <p className="mt-1.5 text-xs text-red-600">{errors.xrayFiles}</p>}
        </div>

        <div className="mt-8 flex items-center justify-end">
          <button
            type="button"
            data-testid="intake-next"
            onClick={onNext}
            className="dyntra-btn-primary py-3 px-6 text-base"
          >
            Continue to guided chat <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      <aside className="rounded-2xl border border-gray-100 bg-white shadow-sm p-6">
        <div className="inline-flex items-center gap-2 rounded-full bg-[#F3EFFC] px-3 py-1 text-xs font-bold text-[#4B2AAD]">
          <Stethoscope className="h-3.5 w-3.5" /> Dr. Dyntra Intake
        </div>
        <h3 className="mt-3 font-display text-lg font-bold text-gray-900">How it works</h3>
        <ol className="mt-3 space-y-2 text-sm text-gray-700">
          <li><b>1.</b> Fill in patient details and the treatment category.</li>
          <li><b>2.</b> Answer a short guided Q&amp;A tailored to the case.</li>
          <li><b>3.</b> We compile the case, run AI analysis, and generate a clinical report you can print or share.</li>
        </ol>
        <p className="mt-4 text-xs text-gray-500 leading-relaxed">
          Draft answers are auto-saved to this browser. AI recommendations are for informed clinical decision-making and do not replace in-person examination.
        </p>
      </aside>
    </section>
  );
}

// ----------------------- Step 2: Chat -----------------------

function ChatStep({ intake, questions, answers, setAnswers, onBack, onSubmit, phase, analysisMsg }) {
  const [cursor, setCursor] = useState(0);
  const [input, setInput] = useState("");
  const [answered, setAnswered] = useState([]); // { q, a }
  const listRef = useRef(null);

  useEffect(() => {
    // resume prior answered state on mount if the parent already has answers
    const pre = [];
    for (const q of questions) {
      const a = answers[q.id];
      if (a != null && a !== "") {
        pre.push({ q, a });
      } else {
        break;
      }
    }
    setAnswered(pre);
    setCursor(pre.length);
    setInput("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [answered, cursor]);

  const currentQ = questions[cursor];
  const isLast = cursor >= questions.length - 1;
  const submitting = phase === "submitting" || phase === "analyzing";

  const commit = () => {
    if (!currentQ || submitting) return;
    const text = input.trim();
    if (!currentQ.optional && text.length === 0) {
      toast.error("Please provide an answer");
      return;
    }
    if (text.length > 2000) {
      toast.error("Answer is too long (max 2000 chars)");
      return;
    }
    setAnswers((prev) => ({ ...prev, [currentQ.id]: text }));
    setAnswered((prev) => [...prev, { q: currentQ, a: text }]);
    setInput("");
    setCursor((c) => c + 1);
  };

  const finish = () => {
    if (input.trim() && currentQ) {
      setAnswers((prev) => ({ ...prev, [currentQ.id]: input.trim() }));
    }
    onSubmit();
  };

  const done = cursor >= questions.length;

  return (
    <section className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 rounded-2xl border border-gray-100 bg-white shadow-sm p-6 sm:p-8 flex flex-col min-h-[520px]">
        <div className="flex items-center gap-2 pb-3 border-b border-gray-100">
          <Bot className="h-5 w-5 text-[#4B2AAD]" />
          <h2 className="font-display text-lg font-bold text-gray-900">Guided AI Intake</h2>
          <span className="ml-auto text-xs text-gray-500">{Math.min(cursor, questions.length)}/{questions.length}</span>
        </div>

        <div ref={listRef} className="flex-1 overflow-y-auto py-4 space-y-3">
          {answered.map((row, i) => (
            <div key={`${row.q.id}-${i}`} className="space-y-2">
              <Bubble side="bot">{row.q.prompt}</Bubble>
              <Bubble side="user">{row.a || <em className="text-gray-500">(skipped)</em>}</Bubble>
            </div>
          ))}
          {!done && currentQ && (
            <Bubble side="bot">{currentQ.prompt}</Bubble>
          )}
          {done && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
              <b>All done.</b> Review your answers above and submit for AI analysis.
            </div>
          )}
          {submitting && (
            <div className="rounded-xl border border-[#4B2AAD]/30 bg-[#F3EFFC] p-4 flex items-center gap-3">
              <Loader2 className="h-4 w-4 animate-spin text-[#4B2AAD]" />
              <span className="text-sm font-semibold text-[#4B2AAD]">{analysisMsg}</span>
            </div>
          )}
        </div>

        <div className="mt-3 border-t border-gray-100 pt-3">
          {!done ? (
            <div className="flex items-end gap-2">
              <textarea
                data-testid="chat-input"
                rows={2}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    commit();
                  }
                }}
                placeholder={currentQ?.placeholder || "Type your answer…"}
                disabled={submitting}
                className="flex-1 rounded-xl border border-gray-200 focus:border-[#4B2AAD] focus:ring-2 focus:ring-[#4B2AAD]/15 px-4 py-2.5 outline-none text-sm resize-none"
              />
              <button
                type="button"
                data-testid="chat-send"
                onClick={commit}
                disabled={submitting}
                className="dyntra-btn-primary h-11 px-4"
                aria-label="Send answer"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              data-testid="chat-submit"
              onClick={finish}
              disabled={submitting}
              className="dyntra-btn-primary w-full py-3 text-base"
            >
              {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> {analysisMsg}</> : <>Submit for AI analysis <ArrowRight className="h-4 w-4" /></>}
            </button>
          )}
          <div className="mt-2 flex items-center justify-between">
            <button
              type="button"
              data-testid="chat-back"
              onClick={onBack}
              disabled={submitting}
              className="text-xs font-semibold text-gray-500 hover:text-gray-700"
            >
              ← Back to intake form
            </button>
            {!done && currentQ?.optional && (
              <button
                type="button"
                data-testid="chat-skip"
                onClick={() => { setInput(""); commit(); }}
                disabled={submitting}
                className="text-xs font-semibold text-[#4B2AAD] hover:underline"
              >
                Skip this question
              </button>
            )}
          </div>
        </div>
      </div>

      <aside className="rounded-2xl border border-gray-100 bg-white shadow-sm p-6 h-fit">
        <p className="text-xs font-bold uppercase tracking-widest text-[#4B2AAD]">Case snapshot</p>
        <h3 className="mt-1 font-display text-lg font-bold text-gray-900">{intake.fullName || "Patient"}</h3>
        <p className="text-sm text-gray-600">{intake.age} yrs · {intake.gender || "-"}</p>
        <dl className="mt-4 space-y-2 text-sm">
          <Row k="Category" v={TREATMENT_CATEGORIES.find((c) => c.value === intake.treatmentCategory)?.label || "-"} />
          <Row k="Chief complaint" v={intake.chiefComplaint} />
          <Row k="BP" v={intake.bloodPressure || "-"} />
          <Row k="X-ray" v={intake.xrayFiles[0]?.name || "—"} />
        </dl>
      </aside>
    </section>
  );
}

function Row({ k, v }) {
  return (
    <div>
      <dt className="text-xs font-bold uppercase tracking-wider text-gray-500">{k}</dt>
      <dd className="text-sm text-gray-900 mt-0.5 leading-snug">{v}</dd>
    </div>
  );
}

function Bubble({ side, children }) {
  const isBot = side === "bot";
  return (
    <div className={`flex ${isBot ? "" : "justify-end"}`}>
      <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${isBot ? "bg-[#F3EFFC] text-gray-800" : "bg-[#4B2AAD] text-white"}`}>
        {children}
      </div>
    </div>
  );
}

// ----------------------- shared inputs -----------------------

function Field({ label, testid, value, onChange, type = "text", placeholder, error, fieldKey }) {
  return (
    <div data-field={fieldKey}>
      <label className="text-xs font-bold uppercase tracking-wider text-gray-500">{label}</label>
      <input
        data-testid={testid}
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className={`mt-1 w-full rounded-xl border ${error ? "border-red-400" : "border-gray-200"} focus:border-[#4B2AAD] focus:ring-2 focus:ring-[#4B2AAD]/15 px-4 py-2.5 outline-none text-sm`}
      />
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}

function SelectField({ label, testid, value, onChange, options, error, fieldKey }) {
  return (
    <div data-field={fieldKey}>
      <label className="text-xs font-bold uppercase tracking-wider text-gray-500">{label}</label>
      <select
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`mt-1 w-full rounded-xl border ${error ? "border-red-400" : "border-gray-200"} focus:border-[#4B2AAD] focus:ring-2 focus:ring-[#4B2AAD]/15 px-4 py-2.5 outline-none text-sm bg-white`}
      >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}
